import React, { useState } from 'react';
import { Heart, DollarSign, X, Sparkles, Send } from 'lucide-react';
import { createTipCheckout, getTipAmounts } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { toast } from 'sonner';

const TIP_PRESETS = [0.50, 1.00, 2.00, 5.00, 10.00];

export function TipButton({ creatorId, creatorName, comicId, variant = 'default' }) {
  const { isAuthenticated, user } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState(2.00);
  const [customAmount, setCustomAmount] = useState('');
  const [message, setMessage] = useState('');
  const [processing, setProcessing] = useState(false);

  const handleTip = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to tip');
      return;
    }

    if (user?.id === creatorId) {
      toast.error("You can't tip yourself!");
      return;
    }

    const amount = customAmount ? parseFloat(customAmount) : selectedAmount;
    
    if (amount < 0.50 || amount > 100) {
      toast.error('Tip must be between $0.50 and $100');
      return;
    }

    try {
      setProcessing(true);
      const res = await createTipCheckout({
        creator_id: creatorId,
        amount,
        message: message || null,
        comic_id: comicId || null,
        is_super_like: false,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = res.data.checkout_url;
    } catch (error) {
      toast.error('Failed to process tip');
      setProcessing(false);
    }
  };

  // Different button variants
  if (variant === 'icon') {
    return (
      <>
        <button
          onClick={() => setShowModal(true)}
          className="p-2 rounded-full bg-pink-500/20 hover:bg-pink-500/30 text-pink-500 transition-colors"
          title={`Tip ${creatorName}`}
        >
          <Heart size={18} />
        </button>
        {showModal && (
          <TipModal
            creatorName={creatorName}
            selectedAmount={selectedAmount}
            setSelectedAmount={setSelectedAmount}
            customAmount={customAmount}
            setCustomAmount={setCustomAmount}
            message={message}
            setMessage={setMessage}
            processing={processing}
            onTip={handleTip}
            onClose={() => setShowModal(false)}
          />
        )}
      </>
    );
  }

  if (variant === 'compact') {
    return (
      <>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-pink-500/20 hover:bg-pink-500/30 text-pink-500 text-sm transition-colors"
        >
          <Heart size={14} />
          <span>Tip</span>
        </button>
        {showModal && (
          <TipModal
            creatorName={creatorName}
            selectedAmount={selectedAmount}
            setSelectedAmount={setSelectedAmount}
            customAmount={customAmount}
            setCustomAmount={setCustomAmount}
            message={message}
            setMessage={setMessage}
            processing={processing}
            onTip={handleTip}
            onClose={() => setShowModal(false)}
          />
        )}
      </>
    );
  }

  // Default variant
  return (
    <>
      <Button
        onClick={() => setShowModal(true)}
        className="bg-pink-500 hover:bg-pink-600"
      >
        <Heart size={16} className="mr-2" />
        Tip {creatorName}
      </Button>
      {showModal && (
        <TipModal
          creatorName={creatorName}
          selectedAmount={selectedAmount}
          setSelectedAmount={setSelectedAmount}
          customAmount={customAmount}
          setCustomAmount={setCustomAmount}
          message={message}
          setMessage={setMessage}
          processing={processing}
          onTip={handleTip}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
}

function TipModal({
  creatorName,
  selectedAmount,
  setSelectedAmount,
  customAmount,
  setCustomAmount,
  message,
  setMessage,
  processing,
  onTip,
  onClose
}) {
  return (
    <div 
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-[var(--surface)] border border-[var(--border)] rounded-xl max-w-md w-full overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-pink-500 to-red-500 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                <Heart size={24} />
              </div>
              <div>
                <h2 className="text-xl font-teko">Tip {creatorName}</h2>
                <p className="text-sm text-white/80">100% goes to the creator</p>
              </div>
            </div>
            <button onClick={onClose} className="p-1 hover:bg-white/20 rounded">
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Amount Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-3">Select Amount</label>
            <div className="grid grid-cols-5 gap-2">
              {TIP_PRESETS.map(amount => (
                <button
                  key={amount}
                  onClick={() => {
                    setSelectedAmount(amount);
                    setCustomAmount('');
                  }}
                  className={`py-2 rounded-lg font-medium transition-colors ${
                    selectedAmount === amount && !customAmount
                      ? 'bg-pink-500 text-white'
                      : 'bg-[var(--background)] hover:bg-pink-500/20 text-[var(--foreground)]'
                  }`}
                >
                  ${amount}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Amount */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">Or enter custom amount</label>
            <div className="relative">
              <DollarSign size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
              <input
                type="number"
                step="0.50"
                min="0.50"
                max="100"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                placeholder="Custom amount"
                className="w-full pl-8 pr-4 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg"
              />
            </div>
          </div>

          {/* Message */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">Add a message (optional)</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Thank you for the amazing content!"
              className="w-full px-4 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg h-20 resize-none"
              maxLength={200}
            />
          </div>

          {/* Summary */}
          <div className="p-4 bg-[var(--background)] rounded-lg mb-6">
            <div className="flex justify-between items-center">
              <span className="text-[var(--text-secondary)]">Tip Amount</span>
              <span className="text-2xl font-teko text-pink-500">
                ${(customAmount ? parseFloat(customAmount) : selectedAmount).toFixed(2)}
              </span>
            </div>
            <p className="text-xs text-[var(--text-secondary)] mt-1">
              No platform fees - creator receives 100%
            </p>
          </div>

          {/* Submit */}
          <Button
            onClick={onTip}
            disabled={processing}
            className="w-full bg-pink-500 hover:bg-pink-600"
          >
            {processing ? (
              'Processing...'
            ) : (
              <>
                <Send size={16} className="mr-2" />
                Send Tip
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Super Like Button - Quick $1 tip with animation
export function SuperLikeButton({ creatorId, creatorName, comicId, episodeId }) {
  const { isAuthenticated } = useAuth();
  const [animating, setAnimating] = useState(false);

  const handleSuperLike = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to Super Like');
      return;
    }

    setAnimating(true);
    
    try {
      const res = await createTipCheckout({
        creator_id: creatorId,
        amount: 1.00,
        comic_id: comicId || null,
        is_super_like: true,
        origin_url: window.location.origin
      });
      
      window.location.href = res.data.checkout_url;
    } catch (error) {
      toast.error('Failed to process Super Like');
      setAnimating(false);
    }
  };

  return (
    <button
      onClick={handleSuperLike}
      className={`relative group flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-pink-500 to-red-500 text-white font-medium transition-all hover:scale-105 ${
        animating ? 'animate-pulse' : ''
      }`}
      title="Super Like ($1)"
    >
      <Sparkles size={16} className={animating ? 'animate-spin' : ''} />
      <span>Super Like</span>
      <span className="text-xs opacity-80">$1</span>
      
      {/* Sparkle effect on hover */}
      <div className="absolute inset-0 rounded-full overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    </button>
  );
}

export default TipButton;
